// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { BottomTabBar } from './BottomTabBar.js';
import { MobileTopBar } from './MobileTopBar.js';
import { MobileAccountTree } from './MobileAccountTree.js';
import { MobileRegister } from './MobileRegister.js';
import { QuickEntry } from './QuickEntry.js';
import { MoreScreen } from './MoreScreen.js';
import { MobileReconcile } from './MobileReconcile.js';
import {
  MobileReportsHub,
  MobileBalanceSheet,
  MobileIncomeStatement,
  MobileTransactionReport,
  MobileNetWorth,
  MobileExpenseBreakdown,
  MobileIncomeVsExpenses,
} from './MobileReports.js';

// Reused desktop screens for secondary surfaces (Phase 7: these largely reflow).
import { GlobalSearch } from '../search/GlobalSearch.js';
import { BudgetManager } from '../budgets/BudgetManager.js';
import { BudgetEditor } from '../budgets/BudgetEditor.js';
import { ScheduledTransactions } from '../scheduled/ScheduledTransactions.js';
import { ImportPage } from '../import/ImportPage.js';
import { Portfolio } from '../investment/Portfolio.js';
import { CapitalGainsReport } from '../investment/CapitalGainsReport.js';
import { CashFlow } from '../reports/CashFlow.js';
import { BudgetReport } from '../reports/BudgetReport.js';
import { CurrencyGainLoss } from '../reports/CurrencyGainLoss.js';
import { Settings } from '../settings/Settings.js';
import { CommodityManager } from '../settings/CommodityManager.js';
import { PriceEditor } from '../settings/PriceEditor.js';
import { ExchangeRateManager } from '../settings/ExchangeRateManager.js';

interface MobileAppProps {
  worker: Worker | null;
  keyData: ArrayBuffer | null;
}

// DECISION (Phase 3): the native shell. A persistent bottom tab bar frames every
// tabbed screen; the two immersive capture/finish flows (quick entry, reconcile)
// go full-screen and hide the tab bar. Redesigned screens (accounts, register,
// reports, quick entry, reconcile, more) are mobile-native; the long tail of
// secondary screens reuses the desktop components inside a mobile top bar, which
// is the right cost/benefit for surfaces that already reflow acceptably.
export function MobileApp({ worker, keyData }: MobileAppProps) {
  const { t } = useTranslation(['mobile', 'reports', 'common']);
  const location = useLocation();
  const fullscreen =
    location.pathname.startsWith('/quick-entry') || location.pathname.startsWith('/reconcile');

  // Mirror the desktop guard: wait for the crypto worker before mounting screens
  // (narrows `worker` to non-null for the reused desktop components that require it).
  if (!worker) {
    return (
      <div className="h-screen flex items-center justify-center bg-bg text-text-muted">
        {t('app.initializing')}
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-bg">
      <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
        <Routes>
          {/* Full-screen capture / finish flows (render their own fixed overlay). */}
          <Route path="/quick-entry" element={<QuickEntry worker={worker} keyData={keyData} />} />
          <Route path="/reconcile/:id" element={<MobileReconcile worker={worker} keyData={keyData} />} />

          {/* Primary redesigned screens. */}
          <Route path="/accounts" element={<MobileAccountTree worker={worker} keyData={keyData} />} />
          <Route path="/accounts/:id" element={<MobileRegister worker={worker} keyData={keyData} />} />
          <Route path="/more" element={<MoreScreen />} />

          {/* Reports — mobile-native summary/drill-down + chart wrappers. */}
          <Route path="/reports" element={<MobileReportsHub />} />
          <Route path="/reports/balance-sheet" element={<MobileBalanceSheet />} />
          <Route path="/reports/income-statement" element={<MobileIncomeStatement />} />
          <Route path="/reports/transactions" element={<MobileTransactionReport />} />
          <Route path="/reports/net-worth" element={<MobileNetWorth />} />
          <Route path="/reports/expense-breakdown" element={<MobileExpenseBreakdown />} />
          <Route path="/reports/income-vs-expenses" element={<MobileIncomeVsExpenses />} />

          {/* Reused desktop screens under a mobile top bar. */}
          <Route path="/search" element={<Reused title={t('common:nav.search')}><GlobalSearch /></Reused>} />
          <Route path="/budgets" element={<Reused title={t('common:nav.budgets')}><BudgetManager worker={worker} keyData={keyData} /></Reused>} />
          <Route path="/budgets/:id" element={<Reused title={t('reused.budget')}><BudgetEditor worker={worker} keyData={keyData} /></Reused>} />
          <Route path="/scheduled-transactions" element={<Reused title={t('common:nav.scheduled')}><ScheduledTransactions worker={worker} keyData={keyData} /></Reused>} />
          <Route path="/import" element={<Reused title={t('common:nav.import')}><ImportPage worker={worker} keyData={keyData} /></Reused>} />
          <Route path="/portfolio" element={<Reused title={t('common:nav.portfolio')}><Portfolio /></Reused>} />
          <Route path="/reports/cash-flow" element={<Reused title={t('reports:titles.cashFlow')}><CashFlow /></Reused>} />
          <Route path="/reports/budget" element={<Reused title={t('reports:titles.budgetReport')}><BudgetReport /></Reused>} />
          <Route path="/reports/capital-gains" element={<Reused title={t('reused.capitalGains')}><CapitalGainsReport /></Reused>} />
          <Route path="/reports/currency-gain-loss" element={<Reused title={t('reused.currencyGainLoss')}><CurrencyGainLoss /></Reused>} />
          <Route path="/settings" element={<Reused title={t('common:nav.settings')}><Settings worker={worker} /></Reused>} />
          <Route path="/settings/commodities" element={<Reused title={t('reused.commodities')}><CommodityManager worker={worker} keyData={keyData} /></Reused>} />
          <Route path="/settings/commodities/:commodityId/prices" element={<Reused title={t('reused.prices')}><PriceEditor worker={worker} keyData={keyData} /></Reused>} />
          <Route path="/settings/exchange-rates" element={<Reused title={t('common:nav.exchangeRates')}><ExchangeRateManager worker={worker} keyData={keyData} /></Reused>} />

          <Route index element={<Navigate to="/accounts" replace />} />
          <Route path="*" element={<Navigate to="/accounts" replace />} />
        </Routes>
      </div>
      {!fullscreen && <BottomTabBar />}
    </div>
  );
}

// Wraps a reused desktop screen with a mobile top bar (title + back) and a flex
// region so the component's own `flex-1` layout fills the screen.
function Reused({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <>
      <MobileTopBar title={title} back />
      <div className="flex-1 flex overflow-hidden">{children}</div>
    </>
  );
}
